import React, { useEffect, useState, useMemo } from "react";
import api from "../../api";
import { toast } from "sonner";
import { ShieldAlert, Ban, ShieldCheck, RefreshCw, AlertTriangle, Monitor, Smartphone, Tablet, ServerCog, Bot } from "lucide-react";
import { BlockIpControls } from "./BlockIpControls";
import { SecurityThresholds } from "./SecurityThresholds";
import { AnteprimaPasswordPanel } from "./AnteprimaPasswordPanel";

const fmtDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso || "—"; }
};

const DEV_ICON = { pc: Monitor, mobile: Smartphone, tablet: Tablet };

const alertCls = (t, blocked) => {
  if (blocked) return "bg-red-600 text-white";
  if (t?.startsWith("Tentativi ripetuti")) return "bg-red-100 text-red-700";
  if (t === "Accesso da IP bloccato") return "bg-red-100 text-red-700";
  if (t === "Bloccato manualmente") return "bg-gray-200 text-gray-700";
  return "bg-amber-100 text-amber-800";
};

const selectCls = "border border-gray-300 rounded-sm px-3 py-2 text-[13px] outline-none focus:border-[#16345c] bg-white";

export const IntrusionsTab = () => {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, blocked: 0 });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/security/flagged-ips")
      .then((r) => { setItems(r.data.items || []); setMeta(r.data); })
      .catch(() => toast.error("Errore nel caricamento"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const shown = useMemo(() => items.filter((i) => filter === "all" || (filter === "blocked" ? i.blocked : !i.blocked)), [items, filter]);

  return (
    <div data-testid="intrusions-tab">
      <SecurityThresholds />
      <AnteprimaPasswordPanel />
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <p className="text-[13px] text-gray-600 max-w-[680px]">
          Indirizzi IP segnalati come tentativi di intrusione (accessi falliti) e IP bloccati. Blocca un IP con un clic: <b>gli IP bloccati non possono accedere all'app</b>. "Segna come legittimo" ripristina l'accesso normale.
        </p>
        <button onClick={load} disabled={loading} data-testid="intrusions-refresh" className="flex items-center gap-2 border border-[#16345c] text-[#16345c] hover:bg-[#16345c] hover:text-white text-[13px] font-bold px-4 py-2 rounded-sm transition-colors disabled:opacity-60">
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Aggiorna
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3"><span className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><ShieldAlert size={20} /></span><div><div className="text-[20px] font-black text-[#16345c] leading-none">{meta.total || 0}</div><div className="text-[11px] text-gray-500 mt-1">IP segnalati</div></div></div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3"><span className="w-10 h-10 rounded-lg bg-red-100 text-red-700 flex items-center justify-center"><Ban size={20} /></span><div><div className="text-[20px] font-black text-[#16345c] leading-none">{meta.blocked || 0}</div><div className="text-[11px] text-gray-500 mt-1">IP bloccati</div></div></div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3"><span className="w-10 h-10 rounded-lg bg-green-100 text-green-700 flex items-center justify-center"><ShieldCheck size={20} /></span><div><div className="text-[20px] font-black text-[#16345c] leading-none">{(meta.total || 0) - (meta.blocked || 0)}</div><div className="text-[11px] text-gray-500 mt-1">Attivi (non bloccati)</div></div></div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="intrusions-filter" className={selectCls}>
          <option value="all">Tutti</option>
          <option value="blocked">Solo bloccati</option>
          <option value="active">Solo attivi</option>
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-testid="intrusions-table">
        <table className="w-full min-w-[860px]">
          <thead>
            <tr className="bg-[#16345c] text-white text-[12px] uppercase tracking-wide">
              <th className="text-left px-4 py-3 font-bold">Indirizzo IP</th>
              <th className="text-left px-4 py-3 font-bold">Data / Ora tentativo</th>
              <th className="text-left px-4 py-3 font-bold">Tipo di alert</th>
              <th className="text-left px-4 py-3 font-bold">Tentativi</th>
              <th className="text-left px-4 py-3 font-bold">Ultimo utente</th>
              <th className="text-left px-4 py-3 font-bold">Stato</th>
              <th className="text-left px-4 py-3 font-bold">Azione</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((it) => {
              const DevIcon = DEV_ICON[it.device] || null;
              return (
                <tr key={it.ip} data-testid={`intrusion-row-${it.ip}`} className={`border-b border-gray-100 last:border-0 transition-colors ${it.blocked ? "bg-red-50 hover:bg-red-100/70" : "hover:bg-gray-50"}`}>
                  <td className="px-4 py-3">
                    <span className="font-mono text-[13px] font-bold text-[#16345c]">{it.ip}</span>
                    {it.kind === "private" && <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-gray-500"><ServerCog size={11} /> interno</span>}
                    {it.whitelisted && <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 align-middle" data-testid={`whitelisted-badge-${it.ip}`}><ShieldCheck size={11} /> fidato</span>}
                  </td>
                  <td className="px-4 py-3 text-[13px] text-gray-700 whitespace-nowrap">{fmtDateTime(it.last_attempt)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${alertCls(it.alert_type, false)}`}><AlertTriangle size={12} /> {it.alert_type}</span>
                    {it.auto && <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-800 text-white align-middle"><Bot size={10} /> AUTO</span>}
                  </td>
                  <td className="px-4 py-3 text-[13px] font-bold text-[#16345c]">{it.attempts || "—"}</td>
                  <td className="px-4 py-3 text-[12.5px] text-gray-600">
                    <span className="inline-flex items-center gap-1">{DevIcon && <DevIcon size={12} className="text-gray-400" />}{it.last_user || "—"}</span>
                  </td>
                  <td className="px-4 py-3">
                    {it.blocked
                      ? <div className="space-y-0.5">
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-600 text-white"><Ban size={12} /> Bloccato</span>
                          <div className="text-[10px] text-gray-500">{it.block_type === "temporary" && it.expires_at ? `fino al ${fmtDateTime(it.expires_at)}` : "permanente"}</div>
                        </div>
                      : <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700"><ShieldCheck size={12} /> Attivo</span>}
                  </td>
                  <td className="px-4 py-3">
                    <BlockIpControls ip={it.ip} blocked={it.blocked} onChanged={load} />
                  </td>
                </tr>
              );
            })}
            {!loading && shown.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400 text-[13px]">Nessun IP segnalato. Ottima notizia: nessun tentativo di intrusione rilevato.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
